import { SetLoading } from '@arema/components/Classes';
import { useAPI, useValidator } from '@arema/components/Hooks';
import { addCommas, bindClick, getNow, getNowLocal } from '@arema/components/Util';
import Validator from '@arema/components/Validator';
import { EventCodeCreate } from '@arema/types/internal';
import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Groupper, Header, Input, Message, Modal, Table, useFrontier } from 'react-frontier2';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EventSectionProps } from '../../AdminClasses';
import { useUser } from '../../AdminHooks';
import API from '../../API';
import UserAccess from '../../UserAccess';

enum ShownModal{
	CREATE = 1,
}
var EventCodeList = (props: EventSectionProps)=>{
	var { Toast } = useFrontier();
	var { t } = useTranslation();
	var { hasAccess } = useUser();
	var { load, data, loadError, setData } = useAPI(API.getEventCodes);
	var [shownModal, setShownModal] = useState<ShownModal>(null);
	var { data: form, prompts, onDataChange, validate, setPrompts, reset: resetForm } = useValidator<EventCodeCreate>({
		description: '',
		type_id: null,
		code_count: null,
		code_uses: null,
		code_prefix: ''
	}, {
		description: [{ rule: 'length', params: [3, 64] }],
		type_id: [
			{ rule: 'empty' },
			{ rule: 'enum', params: [10, 11, 20] },
		],
		code_count: [{ rule: 'number' }, { rule: 'min', params: [1] }],
		code_uses: [{ rule: 'number' }, { rule: 'min', params: [1] }, { rule: 'max', params: [65535] }],
		code_prefix: [{ rule: 'length', params: [4, 4] }]
	})

	useEffect(()=>{
		if(!props.event.dynamic_code) return;
		load(props.event.event_id);
	}, [props.event]);

	var sendCreate = (setLoading: SetLoading)=>{
		var { valid } = validate();
		if(!valid) return;

		setLoading(true);
		API.createEventCodes(props.event.event_id, form).then(res=>{
			if(res.error) return setPrompts([res.message]);
			setData([
				...(data || []),
				{
					group_id: res.data.group_id,
					code_active: form.code_count,
					code_count: form.code_count,
					date_created: getNowLocal().toSeconds(),
					description: form.description
				}
			]);
			resetForm();
			setShownModal(null);
			Toast.success(t('event_codes.group_created'))
		}).catch(err=>{
			return setPrompts([t('common.unexpected_code', { code: 'LCL-EVCDLT-CRT-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}
	
	if(loadError) return <Header text={t('common.error')} subtext={loadError} />
	if(!props.event.dynamic_code){
		return <Header text={t('event_codes.not_active')} subtext={t('event_codes.not_active_text')} iconName={'key'} />
	}
	if(!data) return <Header text={t('common.loading')} loading />
	
	return <div className="ar event-code-list-screen">
		<Table style={{ marginTop: 15, maxWidth: 600 }}>
			<Table.Head
				collapsingIndexes={[0, 2, 3]}
				title={t('event_codes.title')}
				titleRight={hasAccess(UserAccess.EVENTS.CREATE_EVENT_CODES) && (
					<Button text={t('common.add')} iconName={'plus-circle'} size={'tiny'} color={'black'} onClick={bindClick(setShownModal, ShownModal.CREATE)} />
				)}
				data={[t('event_codes.headers', { returnObjects: true })]}
			/>
			<Table.Body>
				{data.length===0 && (
					<Table.Cell row colSpan={100} empty value={t('event_codes.empty')} />
				)}
				{data.map(a=>(
					<Table.Row selectable collapsingIndexes={[0, 2, 3]} key={`EVCD-${a.group_id}`} as={Link} compact to={`/events/${props.event.event_id}/codes/${a.group_id}`}>
						<Table.Cell value={a.group_id} />
						<Table.Cell value={a.description} />
						<Table.Cell>
							{/* <span style={{ fontWeight: 'bold' }}>{addCommas(a.code_active, false)}</span>
							<span style={{ fontSize: 12 }}>/{addCommas(a.code_count, false)}</span> */}
							{addCommas(a.code_count, false)}
						</Table.Cell>
					</Table.Row>
				))}
			</Table.Body>
		</Table>
		<Modal open={shownModal===ShownModal.CREATE} onClose={bindClick(setShownModal)} size={'tiny'}>
			<Modal.Header text={t('event_codes.create.title')} />
			<Modal.Content>
				<Input label={t('event_codes.create.name')} value={form.description} onChange={onDataChange('description')} />
				<Input label={t('event_codes.create.count')} comment={t('event_codes.create.count_comment')} value={form.code_count} onChange={onDataChange('code_count')} />
				<Input label={t('event_codes.create.prefix')} comment={t('event_codes.create.prefix_comment')} value={form.code_prefix} onChange={onDataChange('code_prefix')} maxLength={4} />
				<div style={{ height: 2, backgroundColor: '#f0f0f0', width: '100%', marginBottom: 15, marginTop: 15 }} />
				<Dropdown value={form.type_id} onValueChange={onDataChange('type_id')} label={t('event_codes.create.type')} items={(
					[10, 11, 20].map(a=>(
						{ text: t(`event_codes.types.type_${a}`), value: a }
					))
				)} />
				<Input label={t('event_codes.create.uses')} value={form.code_uses} onChange={onDataChange('code_uses')} />
				{!!form.type_id && !!form.code_uses && !!form.code_count && (
					<Message type='info'>
						<Trans i18nKey={t(`event_codes.create.type_descriptions.type_${form.type_id}`)} values={{ count: addCommas(form.code_uses, false), codes: addCommas(form.code_count, false) }} />
					</Message>
				)}
				<Message type='error' style={{ marginTop: 15 }} list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button basic text={t('common.close')} onClick={bindClick(setShownModal)} />
				<Button text={t('common.continue')} onClick={sendCreate} color={'black'} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default EventCodeList;